import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  getMailbox,
  listHouseholdMail,
  listHouseholdMailSent,
  type MailArrivalApi,
  type MailSentApi,
} from '../lib/api';
import { useRecordsVersion } from '../lib/recordEditor';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { becameNoun, tidyName } from '../lib/proposals';
import './spread.css';
import './householdMail.css';

// A-SERIES — THE MAIL, INSIDE THE APP.
//
// Two of the plan's fifteen features were mail's, and neither had a screen.
// `email.byo_mailbox` read a family's own mailbox every ten minutes; the
// messages filed, were searchable, and appeared nowhere a parent would look.
// `email.outbound` provisioned a real work address on Working and the app
// never sent a single message from it. Both were built, both were green, and
// both were invisible — the same shape seven other features shipped in.
//
// ===================================================================
// THE ROW ANSWERS THE ONLY QUESTION MAIL RAISES: WHAT DID IT BECOME
// ===================================================================
// A mailbox tells you what arrived. That is not what a parent forwarding a
// breakdown at 11pm wants to know — they want to know whether it turned into
// the audition they will be reminded about. So every row carries both halves:
// who sent it and when, then WHAT IT BECAME, and where that lives. A row whose
// second half said nothing would be a mailbox with extra steps, and the
// household already has one of those.
//
// ⚑ AND "KEPT ONLY" IS A REAL, HONEST OUTCOME. A message whose reading is
// above the household's plan is kept in full and not read — the server has
// been writing that sentence (`parse_state: 'not_included'` and its rationale)
// since the ladder shipped, and no screen has ever shown it. It is shown here,
// as what it is: a statement about a plan, never a failure and never an error.
//
// ===================================================================
// TIER HONESTY, THE SAME WAY THE CALENDAR RESOLVES IT
// ===================================================================
// The inbound address is free on every tier, so what arrived at it is the
// family's own record and this list works on Free. What Working buys is the
// WORK ADDRESS — the sending half — so the sent list and the send door are
// `email.outbound`, and below that tier there is one honest line where they
// would be. Gating the arrivals would be withholding what the family already
// handed over.

export interface HouseholdMailProps {
  tenantId: string;
  onOpenAudition: (id: string) => void;
  onOpenShootDay: (id: string) => void;
  onOpenProject: (id: string) => void;
  onOpenRole: (id: string) => void;
  onOpenEngagement: (id: string) => void;
  onOpenSource: (id: string) => void;
  /** G-19 — where an undecided arrival actually IS.
   *
   * This screen has said "Waiting on Today" since it shipped, and until
   * 2026-09-11 Today carried no proposals and the review queue had no door on
   * this pack at all — so the sentence named a destination that did not hold
   * the thing. Today now carries the row, and this is the door that takes a
   * parent to it: a screen that says where something is and cannot take you
   * there is half a sentence. */
  onOpenToday: () => void;
  /** ⚑ THE BUG THIS CLOSES (found live, 2026-09-19): a household forwarded
   * three notices, actioned the one Today happened to show, and had no way
   * at all to reach the other two from THIS screen — the only door into the
   * review queue anywhere in the app was the one on Today
   * (`HouseholdHome.tsx`), and it is reached exactly when Today's OWN read
   * of the queue puts a row in front of you.
   *
   * This is a SECOND, independent door, straight to the full queue — not a
   * substitute for the per-row "Waiting on Today" outcome above, which still
   * opens the fastest path when Today already has the row. Mail is where a
   * parent looks right after forwarding something, and it must not depend
   * on Today's own read of the queue to get them there. */
  onOpenReview: () => void;
}

/** ⚑ THE NOUNS MOVED TO lib/proposals.ts (Day 2 walk, S3.3). Review printed
 * `{"recordType":"aa_audition"}` at a parent while this screen said "an
 * audition" about the SAME proposal — the drift lib/proposals.ts's own header
 * warns about, one screen later. The map, the fallback and `tidyName` now live
 * beside `proposalSummaryLine` and both screens read them.
 *
 * ⚑ A TYPE WITH NO DOOR STILL GETS ITS NOUN. The capture path can create
 * records this pack has no detail screen for (a payment lives inside the money
 * screen; a credit lives on the resume). Saying "a payment" and rendering a
 * flat row is honest; rendering a control that goes nowhere is not, and
 * omitting the outcome would be the worse half of both. */

/** The pieces of a quiet line, joined without a dangling separator when one of
 * them is absent — the A4 lesson, verbatim. */
function joinParts(parts: (string | null)[]): string {
  return parts.filter((p): p is string => Boolean(p && p.trim())).join(' · ');
}

/** Which carrier brought it, in the two terms a family can actually tell
 * apart: one they forwarded to, one they connected. */
function carrierWords(carrier: string | null): string | null {
  if (carrier === 'forwarded') return 'forwarded to your address';
  if (carrier === 'connected_mailbox') return 'from the mailbox you connected';
  return null;
}

/** The sender, split into the name a parent scans for and the address they
 * would reply to.
 *
 * ⚑ ONE LINE FOR THE NAME. A From header is `Dana Okafor
 * <dana@example.invalid>`, and printed whole at 390px it takes two lines
 * before the subject has said anything — three rows of that is a screen you
 * read instead of scan. The address is not dropped: it rides the quiet line
 * with the carrier, which is where somebody checking who actually sent this
 * looks. A From with no display name IS the address, and stays on top. */
function senderParts(from: string | null): { name: string; address: string | null } {
  const raw = (from ?? '').trim();
  if (!raw) return { name: 'An unnamed sender', address: null };
  const angled = /^(.*?)\s*<([^>]+)>$/.exec(raw);
  if (!angled) return { name: raw, address: null };
  const name = angled[1]!.replace(/^["']|["']$/g, '').trim();
  const address = angled[2]!.trim();
  return name ? { name, address } : { name: address, address: null };
}

/** The moment, as a person reads it. Today and yesterday are named because
 * those are the two a parent is actually scanning for; everything else carries
 * its date. Never a fabricated time — an unparseable stamp says so. */
function whenWords(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'no date on it';
  const now = new Date();
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const away = Math.round((today.getTime() - day.getTime()) / 86_400_000);
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (away === 0) return time;
  if (away === 1) return `Yesterday, ${time}`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** One arrival's outcome: the sentence, and the door when there is one.
 *
 * The ORDER is the argument. "Kept, not read" is checked first because it is
 * true of a row whatever its proposal then did — a plan that does not include
 * the reading has already decided the outcome, and reporting the proposal's
 * status over it would describe machinery instead of what happened. */
function outcomeOf(
  arrival: MailArrivalApi,
  doors: HouseholdMailProps
): { say: string; open: (() => void) | null } {
  const openBecame = ((): (() => void) | null => {
    if (!arrival.became) return null;
    const { recordType, recordId } = arrival.became;
    if (recordType === 'aa_audition') return () => doors.onOpenAudition(recordId);
    if (recordType === 'aa_shoot_day') return () => doors.onOpenShootDay(recordId);
    if (recordType === 'aa_project') return () => doors.onOpenProject(recordId);
    if (recordType === 'aa_role') return () => doors.onOpenRole(recordId);
    if (recordType === 'aa_engagement') return () => doors.onOpenEngagement(recordId);
    if (recordType === 'aa_source') return () => doors.onOpenSource(recordId);
    return null;
  })();
  // The original is the fallback door and it always exists for a message that
  // was filed: the family can read what actually arrived, on every tier.
  const openSource = arrival.sourceId ? () => doors.onOpenSource(arrival.sourceId!) : null;

  if (arrival.keptOnly) {
    return { say: 'Kept, not read.', open: openSource };
  }
  // F4b — AND THIS IS THE OTHER HALF OF THAT SENTENCE. Same filing, same
  // "nothing looked at it", opposite ending: something is coming back for
  // this one. It is checked BEFORE 'pending' because a deferred capture is
  // still a pending proposal, and "Waiting on Today" would send a parent to a
  // card to decide about a reading that has not happened.
  if (arrival.notReadYet) {
    return { say: 'Kept — waiting to be read.', open: openSource };
  }
  if (arrival.status === 'pending') {
    // ⚑ THE DOOR IS TODAY, NOT THE ORIGINAL. The row's one control follows
    // its own sentence: this says the decision is waiting on Today, so this
    // is what goes there. The original is not lost — it is the source the
    // proposal's own card opens, one step further in, and every other outcome
    // on this screen still lands on it directly.
    return {
      say: arrival.summary ? `Waiting on Today — ${arrival.summary}` : 'Waiting on Today.',
      open: doors.onOpenToday,
    };
  }
  if (arrival.status === 'approved') {
    if (arrival.became) {
      const name = tidyName(arrival.became.name);
      const noun = becameNoun(arrival.became.recordType);
      return { say: name ? `Became ${noun} — ${name}` : `Became ${noun}`, open: openBecame ?? openSource };
    }
    return { say: 'Filed. Nothing was created from it.', open: openSource };
  }
  if (arrival.status === 'rejected') {
    return { say: 'Kept only — you declined what it proposed.', open: openSource };
  }
  return { say: 'Kept only — the proposal expired undecided.', open: openSource };
}

export function HouseholdMail(props: HouseholdMailProps) {
  const { tenantId } = props;
  const [arrivals, setArrivals] = useState<MailArrivalApi[] | null>(null);
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [address, setAddress] = useState<string | null>(null);
  const [sent, setSent] = useState<MailSentApi[] | null>(null);
  /** The tier refused the sending half. ⚑ THE SERVER IS THE AUTHORITY ON THIS,
   * not `planHas`: a plan read that blinked would otherwise show a Working
   * household the line "On Working, the app sends…" above a list of things it
   * had already sent. The 403 is the fact; the line is what this screen says
   * about it. */
  const [belowTier, setBelowTier] = useState(false);
  const [workAddress, setWorkAddress] = useState<string | null>(null);
  /** The sent half's OWN refusal, said where the list would be. A 403 is the
   * tier and is rendered as the pack's line; anything else is reported in the
   * server's own sentence, because an empty Sent list must never be read as
   * "nothing has gone out". */
  const [sentSay, setSentSay] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const recordsVersion = useRecordsVersion();

  // The address a family would print on a forwarding rule — read from the same
  // door Settings reads it from, so the two can never say different things.
  useEffect(() => {
    let cancelled = false;
    void getMailbox(tenantId)
      .then((a) => {
        if (!cancelled) setAddress(a);
      })
      .catch(() => {
        // Null means SHOW NOTHING (getMailbox's own contract): a printed
        // address that bounces is worse than no address at all.
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, retryNonce]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setFailure(null);
      try {
        const rows = await listHouseholdMail(tenantId);
        if (!cancelled) setArrivals(rows);
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setArrivals(null);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, retryNonce, recordsVersion]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setSentSay(null);
      setBelowTier(false);
      try {
        const result = await listHouseholdMailSent(tenantId);
        if (cancelled) return;
        setWorkAddress(result.workAddress);
        setSent(result.sent);
      } catch (err) {
        if (cancelled) return;
        setSent(null);
        // 403 IS THE TIER AND IS NOT A FAULT. It is answered below with the
        // pack's own line rather than the server's sentence, because a family
        // on Free asked for nothing and is being told off for nothing.
        if (err instanceof ApiError && err.status === 403) {
          setBelowTier(true);
          return;
        }
        const read = describeReadFailure(err);
        setSentSay(`${read.headline} ${read.detail}`);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, retryNonce, recordsVersion]);

  if (failure) {
    return (
      <div class="sp">
        <div class="sp__eyebrow">Mail</div>
        <div class="sp__say">
          {failure.headline}
          <br />
          <span class="sp__say-soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button type="button" class="sp__retry" onClick={() => setRetryNonce((n) => n + 1)}>
            Read the mail again
          </button>
        ) : null}
      </div>
    );
  }

  if (arrivals === null) {
    return (
      <div class="sp">
        <div class="sp__eyebrow">Mail</div>
        <div class="sp__say">One moment…</div>
      </div>
    );
  }

  return (
    <div class="sp">
      <div class="sp__eyebrow">Mail</div>

      <div class="sp__sec">
        <span>Arrived</span>
        {arrivals.length > 0 ? <span>{arrivals.length}</span> : null}
      </div>

      {/* ⚑ THE DOOR THIS SCREEN NEVER HAD. Independent of any one row's own
          outcome — the review queue is reachable from here whether or not
          Today's own read of it happens to show something right now. */}
      <button type="button" class="sp__retry" data-testid="mail-review-door" onClick={props.onOpenReview}>
        Everything waiting for a decision →
      </button>

      {arrivals.length === 0 ? (
        <div class="sp__empty" data-testid="mail-empty">
          {address
            ? `Mail to ${address} lands here, and what it became beside it.`
            : 'You have no address handle yet, so nothing can be forwarded to it.'}
        </div>
      ) : (
        arrivals.map((arrival) => {
          const outcome = outcomeOf(arrival, props);
          const sender = senderParts(arrival.from);
          const provenance = joinParts([sender.address, carrierWords(arrival.carrier)]);
          const inside = (
            <>
              <span class="sp__row-top">
                <span class="sp__row-name">{sender.name}</span>
                <span class="sp__row-when">{whenWords(arrival.arrivedAt)}</span>
              </span>
              <span class="ml__subject">{arrival.subject ?? 'No subject line'}</span>
              {/* WHAT IT BECAME — the half a mailbox cannot answer, and the
                  reason this screen is not one. */}
              <span class="ml__became" data-testid="mail-became">
                {outcome.say}
              </span>
              {provenance ? <span class="sp__row-sub">{provenance}</span> : null}
              {/* THE SENTENCE THE SERVER WROTE AND NO SCREEN SHOWED. For a
                  document above the plan it is the upsell, written since the
                  ladder shipped; for one nothing has read yet it is the
                  promise to try again. Either way it says what was kept, what
                  was not done, and what happens next. */}
              {(arrival.keptOnly || arrival.notReadYet) && arrival.rationale ? (
                <span
                  class="sp__row-sub"
                  data-testid={arrival.keptOnly ? 'mail-kept-only' : 'mail-not-read-yet'}
                >
                  {arrival.rationale}
                </span>
              ) : null}
            </>
          );
          // A row with nowhere to go renders as the statement it is — never a
          // control that does nothing when it is pressed.
          return outcome.open ? (
            <button
              type="button"
              class="sp__row ml__row"
              key={arrival.id}
              data-testid="mail-row"
              onClick={outcome.open}
            >
              {inside}
            </button>
          ) : (
            <div class="sp__row ml__row" key={arrival.id} data-testid="mail-row">
              {inside}
            </div>
          );
        })
      )}

      <div class="sp__sec">
        <span>Sent</span>
        {workAddress ? <span class="hm__addr">{workAddress}</span> : null}
      </div>

      {belowTier ? (
        <p class="sp__craft-say" data-testid="mail-send-teaser">
          On Working, the app sends from your own work address and lists what it sent here.
        </p>
      ) : sentSay ? (
        <p class="sp__craft-say" data-testid="mail-sent-failure">
          {sentSay}
        </p>
      ) : !workAddress ? (
        <p class="sp__craft-say" data-testid="mail-no-work-address">
          You have no work address yet. Settings is where one is set up, and this app
          sends as it once there is one.
        </p>
      ) : (sent ?? []).length === 0 ? (
        <div class="sp__empty" data-testid="mail-sent-empty">
          Nothing has gone out from {workAddress} yet. Every message this app sends is listed here.
        </div>
      ) : (
        (sent ?? []).map((row) => (
          <div class="sp__row ml__row" key={row.id} data-testid="mail-sent-row">
            <span class="sp__row-top">
              <span class="sp__row-name">{row.subject ?? 'No subject line'}</span>
              <span class="sp__row-when">{whenWords(row.sentAt)}</span>
            </span>
          </div>
        ))
      )}
    </div>
  );
}
